import * as React from 'react';
import {useSelector, useDispatch} from 'react-redux';
import {useApi} from 'src/hooks/useApi';
import moment from 'moment';
import type {
  TranslationFunction,
  MakeKeyFunction,
} from 'src/types/language-support';
import useLabelTranslation from 'src/hooks/useLabelTranslation';

import {selectApplicantInformation} from 'src/selectors/crm';
// import {currencies} from 'src/components/crm/lib/filters/candidate-filters/constant';
// import {numberByFormat} from 'src/components/crm/lib/custom-fields/view-custom-fields.jsx';
// import type {MenuOption} from '@spaced-out/ui-design-system/lib/components/Menu';
import {Chip} from '@spaced-out/ui-design-system/lib/components/Chip';
import {Button} from '@spaced-out/ui-design-system/lib/components/Button';
import {Tooltip} from '@spaced-out/ui-design-system/lib/components/Tooltip';
import {CircularLoader} from '@spaced-out/ui-design-system/lib/components/CircularLoader';
import {EmptyState} from '@spaced-out/ui-design-system/lib/components/EmptyState';

// import {Combobox} from '@spaced-out/ui-design-system/lib/components/Combobox';
// import {Input} from '@spaced-out/ui-design-system/lib/components/Input';
import {SemanticIcon} from '@spaced-out/ui-design-system/lib/components/Icon';
// import {RadioButton} from '@spaced-out/ui-design-system/lib/components/RadioButton';

import {
  Card,
  CardHeader,
  CardTitle,
  CardActions,
  CardContent,
} from '@spaced-out/ui-design-system/lib/components/Card';

import {
  BodyMedium,
  BodySmall,
  BodyLarge,
  BodySmallBold,
} from '@spaced-out/ui-design-system/lib/components/Text';

import css from './candidate-fields-card.css';

/**
 * Format field value for display based on field type
 */
const formatFieldValue = (
  field: any,
  value: any,
  t: TranslationFunction,
  makeKey: MakeKeyFunction,
  language: string,
) => {
  if (value === undefined || value === null) {
    return '--';
  }

  switch (field.fieldType) {
    case 'BOOLEAN':
      return value;

    case 'DATE':
      try {
        if (
          typeof value === 'number' ||
          (typeof value === 'string' && /^\d{10}$/.test(value))
        ) {
          return moment
            .unix(Number(value))
            .locale(language)
            .format('DD MMMM, YYYY');
        }
        // Handle date strings like "2025-08-14"
        if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
          return moment(value).locale(language).format('DD MMMM, YYYY');
        }
        // Handle other date formats
        if (value) {
          return moment(value).locale(language).format('DD MMMM, YYYY');
        }
        return '--';
      } catch (e: any) {
        return String(value);
      }

    case 'MULTI_SELECT':
      if (Array.isArray(value)) {
        return value.map((option) => (
          <Chip
            key={option}
            semantic="primary"
            size="medium"
            dismissable={false}
          >
            {option}
          </Chip>
        ));
      }
      return String(value || '');

    case 'SINGLE_ATTACHMENT':
      return value
        ? t('View_attachment', 'View attachment')
        : t('No_attachment', 'No attachment');

    case 'MULTIPLE_ATTACHMENT':
      return Array.isArray(value)
        ? `${value.length} ${t(
            makeKey(`attachment${value.length > 1 ? 's' : ''}`),
            `attachment${value.length > 1 ? 's' : ''}`,
          )}`
        : t('No_attachments', 'No attachments');

    case 'SINGLE_SELECT':
      // Find the human readable value if possible
      if (field.possibleValues && field.possibleValues.length) {
        const possibleValue = field.possibleValues.find(
          // @ts-expect-error - TS7006 - Parameter 'pv' implicitly has an 'any' type.
          (pv) => pv.possibleValue === value,
        );
        return possibleValue ? possibleValue.possibleValue : value;
      }
      return value || t('Not_selected', 'Not selected');

    case 'SINGLE_USER_SELECT':
      return value?.name || value || t('Not_selected', 'Not selected');

    default:
      return String(value || '');
  }
};

const CandidateFieldsCard = ({
  showEditButton = true,
  handleEdit,
}: {
  showEditButton?: boolean;
  handleEdit?: (section: Array<unknown>) => void;
}) => {
  const {t, makeKey, language} = useLabelTranslation();
  const applicantDetails = useSelector(selectApplicantInformation);
  // @ts-expect-error - TS2339 - Property 'customFields' does not exist on type 'Candidate'.
  const {customFields} = applicantDetails ?? {};
  const [isEditing, setIsEditing] = React.useState(false);

  const {result: customFormData, isLoading} = useApi(
    {pathname: '/custom-form?formOf=VIEW_CANDIDATE_FORM'},
    {
      method: 'GET',
      apiPath: '/trmapi/api',
    },
  );

  // if (
  //   (!customFormData?.[0] ||
  //   !customFields ||
  //   !Array.isArray(customFields) ||
  //   customFields.length === 0) &&
  //   !isLoading
  // ) {
  //   return (
  //     <Card
  //       classNames={{
  //         wrapper: css.card,
  //       }}
  //     >
  //       <CardHeader>
  //         <CardTitle className={css.cardTitleContainer}>
  //           <SemanticIcon name="file-alt" size="small" type="solid" />
  //           Custom Fields
  //         </CardTitle>
  //       </CardHeader>
  //       <CardContent>
  //         <BodyMedium>
  //           No custom fields available for this candidate.
  //         </BodyMedium>
  //       </CardContent>
  //     </Card>
  //   );
  // }

  // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type '0' can't be used to index type '{}'.
  const form = customFormData?.[0] || {};

  return (
    <Card
      classNames={{
        wrapper: css.card,
      }}
    >
      <CardHeader>
        <CardTitle className={css.cardTitleContainer}>
          <SemanticIcon name="book" size="small" type="solid" />
          {t('Candidate_Fields', 'Candidate Fields')}
        </CardTitle>
        {showEditButton && handleEdit && (
          <CardActions>
            <Button
              iconLeftName="pen"
              type="ghost"
              size="small"
              onClick={() => {
                // @ts-expect-error - TS7006 - Parameter 'section' implicitly has an 'any' type.
                handleEdit(form.sections.map((section) => section));
              }}
            />
          </CardActions>
        )}
      </CardHeader>
      <CardContent>
        <div className={css.candidateDetailsCardContentContainer}>
          {isLoading ? (
            <div className={css.loadingContainer}>
              <CircularLoader />
            </div>
          ) : // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type '0' can't be used to index type '{}'.
          (!customFormData?.[0] ||
              !customFields ||
              !Array.isArray(customFields) ||
              customFields.length === 0) &&
            !isLoading ? (
            <EmptyState
              title={`${t(
                'No_custom_fields_available_for_this_candidate',
                'No custom fields available for this candidate',
              )}.`}
              description={`${t(
                'Please_add_custom_fields_to_the_candidate_form',
                'Please add custom fields to the candidate form',
              )}.`}
              imageVariant="data"
            />
          ) : (
            Array.isArray(form.sections) &&
            // @ts-expect-error - TS7006 - Parameter 'section' implicitly has an 'any' type. | TS7006 - Parameter 'sectionIndex' implicitly has an 'any' type.
            form.sections.map((section, sectionIndex) => (
              <div key={`section-${section.name}`} className={css.formSection}>
                <div className={css.sectionHeader}>
                  <BodySmallBold color="secondary" className={css.sectionTitle}>
                    {section.name}
                  </BodySmallBold>
                </div>
                <div className={css.sectionFields}>
                  {/* @ts-expect-error - TS7006 - Parameter 'field' implicitly has an 'any' type. | TS7006 - Parameter 'fieldIndex' implicitly has an 'any' type. */}
                  {section.fields.map((field, fieldIndex) => {
                    const customField = field.customField;
                    if (!customField) {
                      return null;
                    }

                    // Find the value for this field
                    const fieldValue = customFields.find(
                      // @ts-expect-error - TS7006 - Parameter 'cf' implicitly has an 'any' type.
                      (cf) => cf.id === customField.id,
                    )?.value;
                    const isRequired = field.show === 'MANDATORY';

                    return (
                      <div className={css.candidateDetailBlock}>
                        <BodySmall color="secondary">
                          {customField.name}
                        </BodySmall>
                        <BodySmall className={css.bold}>
                          {formatFieldValue(
                            customField,
                            fieldValue,
                            t,
                            makeKey,
                            language,
                          )}
                        </BodySmall>
                      </div>
                    );
                  })}
                </div>
                {sectionIndex < form.sections.length - 1 && (
                  <div className={css.sectionDivider}></div>
                )}
              </div>
            ))
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default CandidateFieldsCard;
